import React, { useEffect, useState } from 'react';
import { publicationsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage/ErrorMessage';
import SuccessMessage from '../../components/common/SuccessMessage/SuccessMessage';
import './Publications.css';
import '../../styles/shared/PageHeader.css';
import { errorHandler } from '../../utils/errorHandler';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../services/firebase';
import { STORAGE_PATHS, ROLES } from '../../config/constants';
import { isAdmin, isSuperAdmin } from '../../utils/auth-utils';

function Publications() {
    const [publications, setPublications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState('');
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingPublication, setEditingPublication] = useState(null);
    const [newPublication, setNewPublication] = useState({
        title: '',
        authors: '',
        abstract: '',
        publicationType: 'research',
        publicationDate: '',
        journal: '',
        doi: '',
        keywords: '',
        pdfFile: null,
        coverImage: null
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [searchCategory, setSearchCategory] = useState('all');
    const { currentUser, hasRole } = useAuth();
    const isEditor = hasRole('editor');
    const isAdmin = hasRole('admin');
    const isSuperAdmin = hasRole('superAdmin');

    const fetchPublications = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await publicationsAPI.getAllPublications();

            if (!response) {
                throw new Error('No publications data received');
            }

            const publicationsArray = Array.isArray(response) ? response : [];
            
            setPublications(publicationsArray);
        } catch (error) {
            setError(errorHandler.processError(error).message);
            setPublications([]); // Set empty array on error
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPublications();
    }, []);

    const handleEditClick = (publication) => {
        setEditingPublication({
            ...publication,
            id: publication.id,
            pdfFile: null,
            coverImage: null,
            currentPdfUrl: publication.pdfUrl,
            currentCoverUrl: publication.coverImageUrl
        });
    };

    const handleEditInputChange = (e) => {
        const { name, value } = e.target;
        setEditingPublication(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleEditFileChange = (e, fileType) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                setMessage('File size must be less than 5MB');
                return;
            }

            setEditingPublication(prev => ({
                ...prev,
                [fileType]: file,
                [fileType === 'coverImage' ? 'coverImagePreview' : '']: fileType === 'coverImage' ? URL.createObjectURL(file) : null
            }));
        }
    };

    const handleUpdatePublication = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            setError(null);
            setMessage('');
            
            // Upload new files if selected
            let pdfUrl = editingPublication.currentPdfUrl || '';
            let coverImageUrl = editingPublication.currentCoverUrl || '';

            if (editingPublication.pdfFile) {
                try {
                    const pdfPath = `${STORAGE_PATHS.PUBLICATIONS || 'publications'}/${Date.now()}_${editingPublication.pdfFile.name}`;
                    const pdfRef = ref(storage, pdfPath);
                    
                    // Upload the PDF
                    const pdfUploadResult = await uploadBytes(pdfRef, editingPublication.pdfFile);
                    
                    // Get download URL
                    pdfUrl = await getDownloadURL(pdfUploadResult.ref);
                    
                    setMessage('PDF file updated successfully');
                } catch (pdfError) {
                    setError('Failed to upload PDF file: ' + pdfError.message);
                    setLoading(false);
                    return;
                }
            }

            if (editingPublication.coverImage) {
                try {
                    const imagePath = `${STORAGE_PATHS.PUBLICATIONS || 'publications'}/${Date.now()}_${editingPublication.coverImage.name}`;
                    const coverRef = ref(storage, imagePath);
                    
                    // Upload the image
                    const coverUploadResult = await uploadBytes(coverRef, editingPublication.coverImage);
                    
                    // Get download URL
                    coverImageUrl = await getDownloadURL(coverUploadResult.ref);
                    
                    setMessage(prevMessage => prevMessage ? `${prevMessage} and cover image updated successfully` : 'Cover image updated successfully');
                } catch (imageError) {
                    setError('Failed to upload cover image: ' + imageError.message);
                    setLoading(false);
                    return;
                }
            }
            
            const updateData = {
                title: editingPublication.title,
                authors: editingPublication.authors,
                abstract: editingPublication.abstract,
                publicationType: editingPublication.publicationType,
                publicationDate: editingPublication.publicationDate,
                journal: editingPublication.journal || '',
                doi: editingPublication.doi || '',
                keywords: editingPublication.keywords || '',
                pdfUrl: pdfUrl,
                coverImageUrl: coverImageUrl
            };

            
            await publicationsAPI.updatePublication(editingPublication.id, updateData);
            setMessage('Publication updated successfully!');
            await fetchPublications();
            setEditingPublication(null);
        } catch (error) {
            setError('Failed to update publication. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleDeletePublication = async (id) => {
        if (!window.confirm('Are you sure you want to delete this publication?')) {
            return;
        }

        try {
            setLoading(true);
            setError(null);
            
            const result = await publicationsAPI.deletePublication(id);
            
            if (result && result.message && result.message.includes('successfully')) {
                setMessage('Publication deleted successfully!');
                
                // Update the local state to immediately remove the deleted publication
                setPublications(prevPublications => 
                    prevPublications.filter(pub => pub.id !== id)
                );
                
                // Also refresh from the server to ensure we have the latest data
                await fetchPublications();
            } else {
                setError('Failed to delete publication. Please try again.');
            }
        } catch (error) {
            
            // Handle specific error for non-existing publication
            if (error.code === 'not-found') {
                setError(`Publication not found. It may have been already deleted.`);
                
                // Remove the non-existing publication from the UI
                setPublications(prevPublications => 
                    prevPublications.filter(pub => pub.id !== id)
                );
                
                // Refresh publications list to ensure data consistency
                await fetchPublications();
            } else {
                setError(`Failed to delete publication: ${error.message || 'Unknown error'}`);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleAddPublication = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            setError(null);
            setMessage('');

            // Upload files if they exist
            let pdfUrl = '';
            let coverImageUrl = '';

            if (newPublication.pdfFile) {
                try {
                    const pdfPath = `${STORAGE_PATHS.PUBLICATIONS || 'publications'}/${Date.now()}_${newPublication.pdfFile.name}`;
                    const pdfRef = ref(storage, pdfPath);
                    
                    // Upload the PDF
                    const pdfUploadResult = await uploadBytes(pdfRef, newPublication.pdfFile);
                    
                    // Get download URL
                    pdfUrl = await getDownloadURL(pdfUploadResult.ref);
                    
                    setMessage('PDF file uploaded successfully');
                } catch (pdfError) {
                    setError('Failed to upload PDF file: ' + pdfError.message);
                    setLoading(false);
                    return;
                }
            }

            if (newPublication.coverImage) {
                try {
                    const imagePath = `${STORAGE_PATHS.PUBLICATIONS || 'publications'}/${Date.now()}_${newPublication.coverImage.name}`;
                    const coverRef = ref(storage, imagePath);
                    
                    // Upload the image
                    const coverUploadResult = await uploadBytes(coverRef, newPublication.coverImage);
                    
                    // Get download URL
                    coverImageUrl = await getDownloadURL(coverUploadResult.ref);                    
                    setMessage(prevMessage => prevMessage ? `${prevMessage} and cover image uploaded successfully` : 'Cover image uploaded successfully');
                } catch (imageError) {
                    setError('Failed to upload cover image: ' + imageError.message);
                    setLoading(false);
                    return;
                }
            }

            const publicationData = {
                title: newPublication.title,
                authors: newPublication.authors,
                abstract: newPublication.abstract,
                publicationType: newPublication.publicationType,
                publicationDate: newPublication.publicationDate,
                journal: newPublication.journal,
                doi: newPublication.doi,
                keywords: newPublication.keywords,
                coverImageUrl,
                pdfUrl
            };
            
            await publicationsAPI.createPublication(publicationData);
            
            setNewPublication({
                title: '',
                authors: '',
                abstract: '',
                publicationType: 'research',
                publicationDate: '',
                journal: '',
                doi: '',
                keywords: '',
                pdfFile: null,
                coverImage: null
            });
            
            await fetchPublications();
            setShowAddForm(false);
            setMessage('Publication added successfully!');
        } catch (error) {
            setError('Failed to add publication. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                setMessage('File size must be less than 5MB');
                return;
            }

            const fileType = e.target.id === 'pdf-upload' ? 'pdfFile' : 'coverImage';
            setNewPublication(prev => ({
                ...prev,
                [fileType]: file,
                [fileType === 'coverImage' ? 'coverImagePreview' : '']: fileType === 'coverImage' ? URL.createObjectURL(file) : null
            }));
        }
    };

    const filteredPublications = Array.isArray(publications) ? publications.filter(publication => {
        const searchLower = searchTerm.toLowerCase();
        
        if (searchTerm === '') return true;
        
        switch (searchCategory) {
            case 'title':
                return publication.title.toLowerCase().includes(searchLower);
            case 'authors':
                return publication.authors.toLowerCase().includes(searchLower);
            case 'keywords':
                return publication.keywords?.toLowerCase().includes(searchLower);
            case 'all':
            default:
                return (
                    publication.title.toLowerCase().includes(searchLower) ||
                    publication.authors.toLowerCase().includes(searchLower) ||
                    publication.keywords?.toLowerCase().includes(searchLower) ||
                    publication.abstract.toLowerCase().includes(searchLower)
                );
        }
    }) : [];

    // Clean up preview URLs when component unmounts or when modal closes
    useEffect(() => {
        return () => {
            if (newPublication.coverImagePreview) {
                URL.revokeObjectURL(newPublication.coverImagePreview);
            }
            if (editingPublication?.coverImagePreview) {
                URL.revokeObjectURL(editingPublication.coverImagePreview);
            }
        };
    }, [newPublication.coverImagePreview, editingPublication?.coverImagePreview]);

    if (loading) return <div className="publications-loading"><LoadingSpinner /></div>;

    return (
        <div className="publications-container">
            <div className="page-header">
                <div className="page-header-content">
                    <h1>Our Publications</h1>
                    <p className="page-header-description">
                        Explore our research papers, articles, and publications contributing to Mediterranean youth development.
                    </p>
                    {(isAdmin || isEditor) && (
                        <div className="page-header-buttons">
                            <button 
                                className="page-header-button"
                                onClick={() => setShowAddForm(true)}
                            >
                                <i className="fas fa-plus"></i> Add New Publication
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {error && <ErrorMessage message={error} />}
            {message && <SuccessMessage message={message} />}

            <div className="search-section">
                <div className="search-container">
                    <div className="search-input-wrapper">
                        <i className="fas fa-search search-icon"></i>
                        <input
                            type="text"
                            placeholder="Search publications..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <select
                        value={searchCategory}
                        onChange={(e) => setSearchCategory(e.target.value)}
                    >
                        <option value="all">All Fields</option>
                        <option value="title">Title</option>
                        <option value="authors">Authors</option>
                        <option value="keywords">Keywords</option>
                    </select>
                </div>
            </div>

            {showAddForm && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h2>Add New Publication</h2>
                            <button 
                                className="modal-close-button"
                                onClick={() => setShowAddForm(false)}
                            >
                                ×
                            </button>
                        </div>
                        <div className="modal-body">
                            <form onSubmit={handleAddPublication}>
                                <div className="form-group">
                                    <label htmlFor="title">Title</label>
                                    <input
                                        type="text"
                                        id="title"
                                        value={newPublication.title}
                                        onChange={(e) => setNewPublication(prev => ({...prev, title: e.target.value}))}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Authors</label>
                                    <input
                                        type="text"
                                        name="authors"
                                        value={newPublication.authors}
                                        onChange={(e) => setNewPublication(prev => ({...prev, authors: e.target.value, author: e.target.value}))}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Abstract</label>
                                    <textarea
                                        name="abstract"
                                        value={newPublication.abstract}
                                        onChange={(e) => setNewPublication(prev => ({...prev, abstract: e.target.value, content: e.target.value}))}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Publication Type</label>
                                    <select
                                        name="publicationType"
                                        value={newPublication.publicationType}
                                        onChange={(e) => setNewPublication(prev => ({...prev, publicationType: e.target.value}))}
                                    >
                                        <option value="research">Research Paper</option>
                                        <option value="article">Article</option>
                                        <option value="report">Report</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Publication Date</label>
                                    <input
                                        type="date"
                                        name="publicationDate"
                                        value={newPublication.publicationDate}
                                        onChange={(e) => setNewPublication(prev => ({...prev, publicationDate: e.target.value, date: e.target.value}))}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Journal</label>
                                    <input
                                        type="text"
                                        name="journal"
                                        value={newPublication.journal}
                                        onChange={(e) => setNewPublication(prev => ({...prev, journal: e.target.value}))}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>DOI</label>
                                    <input
                                        type="text"
                                        name="doi"
                                        value={newPublication.doi}
                                        onChange={(e) => setNewPublication(prev => ({...prev, doi: e.target.value}))}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Keywords</label>
                                    <input
                                        type="text"
                                        name="keywords"
                                        value={newPublication.keywords}
                                        onChange={(e) => setNewPublication(prev => ({...prev, keywords: e.target.value}))}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>PDF File</label>
                                    <div className="file-upload-container">
                                        <input
                                            type="file"
                                            accept=".pdf"
                                            onChange={handleFileChange}
                                            id="pdf-upload"
                                            className="file-input"
                                        />
                                        <label htmlFor="pdf-upload" className="file-upload-button">
                                            <i className="fas fa-upload"></i> Choose PDF File
                                        </label>
                                        {newPublication.pdfFile && (
                                            <div className="file-info">
                                                <span className="file-name">
                                                    <i className="fas fa-file-pdf"></i> {newPublication.pdfFile.name}
                                                </span>
                                                <span className="file-size">
                                                    ({(newPublication.pdfFile.size / 1024 / 1024).toFixed(2)} MB)
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label>Cover Image</label>
                                    <div className="file-upload-container">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleFileChange}
                                            id="image-upload"
                                            className="file-input"
                                        />
                                        <label htmlFor="image-upload" className="file-upload-button">
                                            <i className="fas fa-upload"></i> Choose Image
                                        </label>
                                        {newPublication.coverImage && (
                                            <div className="file-info">
                                                <span className="file-name">
                                                    <i className="fas fa-image"></i> {newPublication.coverImage.name}
                                                </span>
                                                <span className="file-size">
                                                    ({(newPublication.coverImage.size / 1024 / 1024).toFixed(2)} MB)
                                                </span>
                                            </div>
                                        )}
                                        {newPublication.coverImagePreview && (
                                            <div className="image-preview">
                                                <img src={newPublication.coverImagePreview} alt="Preview" />
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="modal-actions">
                                    <button type="button" onClick={() => setShowAddForm(false)}>
                                        Cancel
                                    </button>
                                    <button type="submit">Add Publication</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {editingPublication && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h2>Edit Publication</h2>
                            <button 
                                className="modal-close-button"
                                onClick={() => setEditingPublication(null)}
                            >
                                ×
                            </button>
                        </div>
                        <div className="modal-body">
                            <form onSubmit={handleUpdatePublication}>
                                <div className="form-group">
                                    <label htmlFor="edit-title">Title</label>
                                    <input
                                        type="text"
                                        id="edit-title"
                                        value={editingPublication.title}
                                        onChange={handleEditInputChange}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Authors</label>
                                    <input
                                        type="text"
                                        name="authors"
                                        value={editingPublication.authors}
                                        onChange={handleEditInputChange}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Abstract</label>
                                    <textarea
                                        name="abstract"
                                        value={editingPublication.abstract}
                                        onChange={handleEditInputChange}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Publication Type</label>
                                    <select
                                        name="publicationType"
                                        value={editingPublication.publicationType}
                                        onChange={handleEditInputChange}
                                    >
                                        <option value="research">Research Paper</option>
                                        <option value="article">Article</option>
                                        <option value="report">Report</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Publication Date</label>
                                    <input
                                        type="date"
                                        name="publicationDate"
                                        value={editingPublication.publicationDate}
                                        onChange={handleEditInputChange}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Journal</label>
                                    <input
                                        type="text"
                                        name="journal"
                                        value={editingPublication.journal}
                                        onChange={handleEditInputChange}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>DOI</label>
                                    <input
                                        type="text"
                                        name="doi"
                                        value={editingPublication.doi}
                                        onChange={handleEditInputChange}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Keywords</label>
                                    <input
                                        type="text"
                                        name="keywords"
                                        value={editingPublication.keywords}
                                        onChange={handleEditInputChange}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>PDF File</label>
                                    <div className="file-upload-container">
                                        <input
                                            type="file"
                                            accept=".pdf"
                                            onChange={(e) => handleEditFileChange(e, 'pdfFile')}
                                            id="pdf-edit-upload"
                                            className="file-input"
                                        />
                                        <label htmlFor="pdf-edit-upload" className="file-upload-button">
                                            <i className="fas fa-upload"></i> Choose PDF File
                                        </label>
                                        {editingPublication.currentPdfUrl && (
                                            <span className="file-name">{editingPublication.currentPdfUrl}</span>
                                        )}
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label>Cover Image</label>
                                    <div className="file-upload-container">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => handleEditFileChange(e, 'coverImage')}
                                            id="image-edit-upload"
                                            className="file-input"
                                        />
                                        <label htmlFor="image-edit-upload" className="file-upload-button">
                                            <i className="fas fa-upload"></i> Choose Image
                                        </label>
                                        {editingPublication.currentCoverUrl && !editingPublication.coverImagePreview && (
                                            <div className="image-preview">
                                                <img src={editingPublication.currentCoverUrl} alt="Current cover" />
                                            </div>
                                        )}
                                        {editingPublication.coverImagePreview && (
                                            <div className="image-preview">
                                                <img src={editingPublication.coverImagePreview} alt="New preview" />
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="modal-actions">
                                    <button type="button" onClick={() => setEditingPublication(null)}>
                                        Cancel
                                    </button>
                                    <button type="submit">Update Publication</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            <div className="publications-list">
                {filteredPublications.length === 0 ? (
                    <div className="no-publications">
                        <p>No publications found.</p>
                        {searchTerm && (
                            <p>Try adjusting your search criteria.</p>
                        )}
                    </div>
                ) : (
                    filteredPublications.map(publication => (
                        <div key={publication.id} className="publication-card">
                            {publication.coverImageUrl && (
                                <div className="publication-cover">
                                    <img src={publication.coverImageUrl} alt={publication.title} />
                                </div>
                            )}
                            <div className="publication-header">
                                <h3>{publication.title}</h3>
                                {(isEditor || isAdmin) && (
                                    <div className="publication-actions">
                                        <button onClick={() => handleEditClick(publication)}>
                                            <i className="fas fa-edit"></i> Edit
                                        </button>
                                        {isSuperAdmin && (
                                            <button onClick={() => handleDeletePublication(publication.id)}>
                                                <i className="fas fa-trash"></i> Delete
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div className="publication-meta">
                                <p><strong>Authors:</strong> {publication.authors}</p>
                                <p><strong>Type:</strong> {publication.publicationType}</p>
                                <p><strong>Date:</strong> {publication.publicationDate}</p>
                                {publication.journal && <p><strong>Journal:</strong> {publication.journal}</p>}
                                {publication.doi && <p><strong>DOI:</strong> {publication.doi}</p>}
                            </div>
                            <div className="publication-abstract">
                                <h4>Abstract</h4>
                                <p>{publication.abstract}</p>
                            </div>
                            {publication.keywords && (
                                <div className="publication-keywords">
                                    <strong>Keywords:</strong> {publication.keywords}
                                </div>
                            )}
                            {publication.pdfUrl && (
                                <div className="publication-actions">
                                    <a href={publication.pdfUrl} target="_blank" rel="noopener noreferrer" className="download-button">
                                        <i className="fas fa-download"></i> Download PDF
                                    </a>
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}

export default Publications;